import React from 'react';
import { BarChart3, Trophy, Dices, Gamepad2, Settings as SettingsIcon, Home, ChevronRight, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Dashboard Component
 * Home page showing an overview of all available sections
 *
 * Features:
 * - Accessible card grid layout
 * - ARIA labels for icons
 * - Semantic HTML structure
 * - Mobile-responsive design
 * - Quick reference guide
 * - Click handlers for navigation
 */
const Dashboard = ({ activeTab, setActiveTab }) => {
  const { t } = useTranslation();

  const sections = [
    {
      id: 'football',
      title: t('footballLabel'),
      description: t('footballStatisticsAndAnalysis'),
      icon: Trophy,
      color: 'bg-primary',
      isCategory: true,
      subsections: [
        {
          id: 1,
          title: t('tabChampionships'),
          description: t('viewFootballChampionshipsAndMatchStatistics'),
          icon: Trophy,
          color: 'bg-primary',
          ariaLabel: 'Navigate to Championships section',
          tabIndex: 1
        },
        {
          id: 2,
          title: t('tabTeamStats'),
          description: t('analyzeTeamPerformanceAndStatistics'),
          icon: BarChart3,
          color: 'bg-success',
          ariaLabel: 'Navigate to Team Stats section',
          tabIndex: 2
        }
      ]
    },
    {
      id: 'fantasy',
      title: t('fantasy.title'),
      description: t('fantasy.description'),
      icon: Users,
      color: 'bg-warning',
      isCategory: true,
      subsections: [
        {
          id: 10,
          title: t('fantasy.players.title'),
          description: t('fantasy.players.name'),
          icon: Users,
          color: 'bg-info',
          ariaLabel: 'Navigate to Fantasy Players section',
          tabIndex: 10
        },
        {
          id: 11,
          title: t('fantasy.stats.title'),
          description: t('fantasy.stats.gameweek'),
          icon: BarChart3,
          color: 'bg-success',
          ariaLabel: 'Navigate to Fantasy Stats section',
          tabIndex: 11
        },
        {
          id: 12,
          title: t('fantasy.fixtures.title'),
          description: t('fantasy.fixtures.gameweek'),
          icon: Trophy,
          color: 'bg-primary',
          ariaLabel: 'Navigate to Fantasy Fixtures section',
          tabIndex: 12
        },
        {
          id: 13,
          title: t('fantasy.leaderboard.title'),
          description: t('fantasy.leaderboard.fantasy_points'),
          icon: Trophy,
          color: 'bg-danger',
          ariaLabel: 'Navigate to Fantasy Leaderboard section',
          tabIndex: 13
        }
      ]
    },
    {
      id: 'lottery',
      title: t('lottery'),
      description: t('lotteryResultsAndPredictions'),
      icon: Dices,
      color: 'bg-danger',
      isCategory: true,
      subsections: [
        {
          id: 3,
          title: t('titlePageTabLotto'),
          description: t('trackLottoLotteryResultsByWheel'),
          icon: Dices,
          color: 'bg-secondary',
          ariaLabel: 'Navigate to Lotto section',
          tabIndex: 3
        },
        {
          id: 4,
          title: t('titlePageTabWinHause'),
          description: t('trackWinHouseLotteryResults'),
          icon: Dices,
          color: 'bg-info',
          ariaLabel: 'Navigate to Win House section',
          tabIndex: 4
        },
        {
          id: 5,
          title: t('titlePageTabSuperEnalotto'),
          description: t('analyzeSuperEnalottoLotteryPredictions'),
          icon: Dices,
          color: 'bg-success',
          ariaLabel: 'Navigate to Super Enalotto section',
          tabIndex: 5
        }
      ]
    },
    {
      id: 6,
      title: t('gameOnline'),
      description: t('play24DifferentBrowserBasedGames'),
      icon: Gamepad2,
      color: 'bg-danger',
      ariaLabel: 'Navigate to Game Online section to play various games',
      tabIndex: 6
    },
  ];

  return (
    <div className="dashboard-container">
      {/* Dashboard Header */}
      <header className="dashboard-header">
        <div className="d-flex align-items-center gap-3 mb-0">
          <Home size={40} className="text-white" aria-hidden="true" />
          <div>
            <h1 className="mb-0">{t('dashboardLabel')}</h1>
            <p className="mb-0 text-white-50">{t('welcomeToGameStatsSelectASectionBelow')}</p>
          </div>
        </div>
      </header>

      {/* Dashboard Grid */}
      <section className="dashboard-sections" aria-label="Available sections">
        {sections.map((section) => {
          const IconComponent = section.icon;

          if (section.isCategory) {
            // Render category with subsections
            return (
              <div key={section.id} className="dashboard-category">
                {/* Category Header */}
                <div className="category-header">
                  <div className={`dashboard-card-icon ${section.color}`}>
                    <IconComponent size={32} aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="category-title">{section.title}</h2>
                    <p className="category-description">{section.description}</p>
                  </div>
                </div>

                {/* Subsections Grid */}
                <div className="subsections-grid">
                  {section.subsections && section.subsections.map((subsection) => {
                    const SubIconComponent = subsection.icon;
                    return (
                      <article key={subsection.id} className="dashboard-card subsection-card">
                        <div className={`dashboard-card-icon ${subsection.color}`}>
                          <SubIconComponent size={32} aria-hidden="true" />
                        </div>
                        <h3 className="dashboard-card-title">{subsection.title}</h3>
                        <p className="dashboard-card-description">{subsection.description}</p>
                        <div className="dashboard-card-footer">
                          <button
                            className="badge bg-primary rounded-pill border-0"
                            onClick={() => setActiveTab(subsection.tabIndex)}
                            aria-label={subsection.ariaLabel}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setActiveTab(subsection.tabIndex);
                              }
                            }}
                          >
                            {t('viewSection')}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            );
          } else {
            // Render standalone card
            return (
              <article key={section.id} className="dashboard-card standalone-card">
                <div className={`dashboard-card-icon ${section.color}`}>
                  <IconComponent size={32} aria-hidden="true" />
                </div>
                <h2 className="dashboard-card-title">{section.title}</h2>
                <p className="dashboard-card-description">{section.description}</p>
                <div className="dashboard-card-footer">
                  <button
                    className="badge bg-primary rounded-pill border-0"
                    onClick={() => setActiveTab(section.tabIndex)}
                    aria-label={section.ariaLabel}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveTab(section.tabIndex);
                      }
                    }}
                  >
                    {t('viewSection')}
                  </button>
                </div>
              </article>
            );
          }
        })}
      </section>

      {/* Dashboard Info Section */}
      <section className="dashboard-info mt-5">
        <div className="row g-4">
          <div className="col-12 col-md-6">
            <article className="info-box">
              <h3 className="mb-3">📊 {t('quickStats')}</h3>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <strong>{t('footballLabel')}:</strong> {t('tabChampionships')}, {t('tabTeamStats')} &amp; {t('fantasy.title')}
                </li>
                <li className="mb-2">
                  <strong>{t('fantasy.title')}:</strong> {t('fantasy.players.title')}, {t('fantasy.stats.title')}, {t('fantasy.fixtures.title')} &amp; {t('fantasy.leaderboard.title')}
                </li>
                <li className="mb-2">
                  <strong>{t('lottery')}:</strong> {t('titlePageTabLotto')}, Win House &amp; {t('titlePageTabSuperEnalotto')}
                </li>
                <li className="mb-2">
                  <strong>{t('gameOnline')}:</strong> 24 {t('browserBasedGames')}
                </li>
                <li>
                  <strong>{t('settingsLabel')}:</strong> {t('preferencesAndAccessControl')}
                </li>
              </ul>
            </article>
          </div>
          <div className="col-12 col-md-6">
            <article className="info-box">
              <h3 className="mb-3">✨ {t('howToUse')}</h3>
              <ul className="list-unstyled">
                <li className="mb-2">
                  {t('useTheMenuButtonInTheTopLeftCornerToNavigate')}
                </li>
                <li className="mb-2">
                  {t('clickAnySectionAboveToViewDetailedInformation')}
                </li>
                <li className="mb-2">
                  {t('dataUpdatesInRealTimeFromTheBackendApi')}
                </li>
                <li>
                  {t('worksOnAllDevicesMobileTabletAndDesktop')}
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
